import { useState, useEffect } from 'react';
import { Layout } from '@/shared/components/Layout';
import { api } from '@/shared/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { captureError } from '@/shared/lib/monitoring';
import { DictionaryEditor } from '@/routes/AdminDictPage';

const API_BASE = import.meta.env.VITE_API_URL || '';

// API через пароль (для доступа вне Telegram)
async function adminApi(path: string, opts: RequestInit = {}) {
  const token = localStorage.getItem('admin_token');
  const res = await fetch(`${API_BASE}/api/admin${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', 'X-Admin-Password': token || '', ...opts.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

function LoginScreen({ onLogin }: { onLogin: () => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (data.ok) {
        localStorage.setItem('admin_token', data.token);
        onLogin();
      } else {
        setError(data.error || 'Ошибка');
      }
    } catch {
      setError('Ошибка подключения');
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0c0c18' }}>
      <form onSubmit={handleSubmit} style={{
        background: '#151525', borderRadius: 16, padding: 32, width: 340,
        border: '1px solid rgba(255,255,255,0.08)',
      }}>
        <h1 style={{ color: '#fff', fontSize: 20, fontWeight: 600, marginBottom: 24, textAlign: 'center' }}>
          🛡️ 2EnBota Admin
        </h1>
        <input
          type="password" value={password} onChange={e => setPassword(e.target.value)}
          placeholder="Пароль" autoFocus
          style={{
            width: '100%', padding: '12px 14px', background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 10, color: '#fff', fontSize: 14, marginBottom: 16, outline: 'none', boxSizing: 'border-box',
          }}
        />
        {error && <div style={{ color: '#ff5252', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <button type="submit" style={{
          width: '100%', padding: '12px', background: '#8B5CF6', color: '#fff',
          border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
        }}>
          Войти
        </button>
      </form>
    </div>
  );
}

type AdminTab = 'stats' | 'users' | 'flags' | 'promos' | 'dictionary';

export function AdminPage() {
  const { user } = useAuthStore();
  const isTelegramAdmin = !!user?.is_admin;
  const [passwordAuthed, setPasswordAuthed] = useState(!!localStorage.getItem('admin_token'));
  const isAuthed = isTelegramAdmin || passwordAuthed;

  // Если не авторизован ни через Telegram, ни через пароль — показываем логин
  if (!isAuthed) {
    return <LoginScreen onLogin={() => setPasswordAuthed(true)} />;
  }

  return <AdminPanel useTelegramApi={isTelegramAdmin} />;
}

function AdminPanel({ useTelegramApi }: { useTelegramApi: boolean }) {
  // Обёрка для API вызовов — Telegram auth или пароль
  const fetchData = async (path: string) => {
    if (useTelegramApi) return api.get<any>(path);
    return adminApi(path.replace('/admin', ''));
  };

  const VALID_TABS: AdminTab[] = ['stats', 'users', 'flags', 'promos', 'dictionary'];

  const [tab, setTabState] = useState<AdminTab>(() => {
    const hash = window.location.hash.replace('#', '') as AdminTab;
    if (VALID_TABS.includes(hash)) return hash;
    const saved = localStorage.getItem('admin_active_tab') as AdminTab;
    if (VALID_TABS.includes(saved)) return saved;
    return 'stats';
  });

  const setTab = (newTab: AdminTab) => {
    setTabState(newTab);
    localStorage.setItem('admin_active_tab', newTab);
    window.location.hash = newTab;
  };
  const [stats, setStats] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [flags, setFlags] = useState<any[]>([]);
  const [promos, setPromos] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Форма нового промокода
  const [newPromo, setNewPromo] = useState({ code: '', plan: 'premium', duration_days: 30, max_uses: '' });
  const [promoMessage, setPromoMessage] = useState('');

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    if (tab === 'stats') loadStats();
    if (tab === 'users') loadUsers();
    if (tab === 'flags') loadFlags();
    if (tab === 'promos') loadPromos();
  }, [tab]);

  const loadStats = async () => {
    try {
      const data = await fetchData('/admin/stats');
      setStats(data);
    } catch (err) { captureError(err, { context: 'AdminPage.loadStats' }); }
  };

  const loadUsers = async () => {
    try {
      const data = await fetchData('/admin/users');
      setUsers(data.users);
    } catch (err) { captureError(err, { context: 'AdminPage.loadUsers' }); }
  };

  const loadFlags = async () => {
    try {
      const data = await fetchData('/admin/feature-flags');
      setFlags(data);
    } catch (err) { captureError(err, { context: 'AdminPage.loadFlags' }); }
  };

  const loadPromos = async () => {
    try {
      const data = await fetchData('/admin/promo-codes');
      setPromos(data);
    } catch (err) { captureError(err, { context: 'AdminPage.loadPromos' }); }
  };


  const postData = async (path: string, body: any) => {
    if (useTelegramApi) return api.post<any>(path, body);
    return adminApi(path.replace('/admin', ''), { method: 'POST', body: JSON.stringify(body) });
  };
  const putData = async (path: string, body: any) => {
    if (useTelegramApi) return api.put<any>(path, body);
    return adminApi(path.replace('/admin', ''), { method: 'PUT', body: JSON.stringify(body) });
  };

  const toggleFlag = async (key: string, field: 'free_access' | 'premium_access', current: boolean) => {
    try {
      await putData(`/admin/feature-flags/${key}`, { [field]: !current });
      setFlags(prev => prev.map(f => f.key === key ? { ...f, [field]: !current } : f));
    } catch (err: any) {
      alert(err.message);
    }
  };

  const togglePromo = async (id: number) => {
    try {
      const updated = await postData(`/admin/promo-codes/${id}/toggle`, {});
      setPromos(prev => prev.map(p => p.id === id ? { ...p, is_active: updated.is_active } : p));
    } catch (err: any) {
      alert(err.message);
    }
  };

  const createPromo = async () => {
    setPromoMessage('');
    try {
      await postData('/admin/promo-codes', {
        code: newPromo.code,
        plan: newPromo.plan,
        duration_days: Number(newPromo.duration_days),
        max_uses: newPromo.max_uses ? Number(newPromo.max_uses) : undefined,
      });
      setPromoMessage('✅ Промокод создан');
      setNewPromo({ code: '', plan: 'premium', duration_days: 30, max_uses: '' });
      loadPromos();
    } catch (err: any) {
      setPromoMessage(`❌ ${err.message}`);
    }
  };

  const SIDEBAR_ITEMS: { key: AdminTab; icon: string; label: string }[] = [
    { key: 'stats', icon: '📊', label: 'Статистика' },
    { key: 'users', icon: '👥', label: 'Пользователи' },
    { key: 'flags', icon: '🚩', label: 'Feature Flags' },
    { key: 'promos', icon: '🎟️', label: 'Промокоды' },
    { key: 'dictionary', icon: '📖', label: 'Словарь' },
  ];

  const [serviceStatus, setServiceStatus] = useState<Record<string, boolean>>({});

  // Проверка статусов сервисов
  useEffect(() => {
    const checkServices = async () => {
      const checks: Record<string, string> = {
        API: `${API_BASE}/api/health`,
        Web: `${API_BASE}/`,
      };
      const results: Record<string, boolean> = {};
      for (const [name, url] of Object.entries(checks)) {
        try {
          const res = await fetch(url, { method: 'GET', signal: AbortSignal.timeout(5000) });
          results[name] = res.ok;
        } catch { results[name] = false; }
      }
      setServiceStatus(results);
    };
    checkServices();
    const interval = setInterval(checkServices, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    window.location.reload();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw', overflow: 'hidden', background: 'var(--theme-bg, #0f0f1a)', color: 'var(--theme-text)' }}>

        {/* Titlebar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 20px', borderBottom: '1px solid var(--theme-border)',
          background: 'var(--theme-bg)', flexShrink: 0,
        }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>🛡️ 2EnBota Admin</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Статусы сервисов */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {Object.entries(serviceStatus).map(([name, ok]) => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: ok ? '#00e676' : '#ff5252',
                    boxShadow: ok ? '0 0 6px #00e676' : '0 0 6px #ff5252',
                  }} />
                  <span style={{ color: 'var(--theme-text-secondary)' }}>{name}</span>
                </div>
              ))}
            </div>
            {/* Кнопка выйти */}
            <button onClick={handleLogout} style={{
              padding: '5px 12px', background: 'rgba(255,82,82,0.1)',
              border: '1px solid rgba(255,82,82,0.3)', borderRadius: 6,
              color: '#ff5252', fontSize: 12, cursor: 'pointer',
            }}>
              Выйти
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>

          {/* Sidebar */}
          <div style={{
            width: 200, flexShrink: 0,
            background: 'var(--theme-bg)',
            borderRight: '1px solid var(--theme-border)',
            padding: '12px 0', overflowY: 'auto',
          }}>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {SIDEBAR_ITEMS.map(item => (
                <button
                  key={item.key}
                  onClick={() => setTab(item.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 16px', border: 'none', cursor: 'pointer',
                    background: tab === item.key ? 'var(--theme-primary)' : 'transparent',
                    color: tab === item.key ? '#fff' : 'var(--theme-text-secondary)',
                    fontWeight: tab === item.key ? 600 : 400,
                    fontSize: 14, textAlign: 'left', width: '100%',
                    borderRadius: 0,
                    transition: 'background 0.15s, color 0.15s',
                  }}
                >
                  <span style={{ fontSize: 16, width: 22, textAlign: 'center' }}>{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto', minHeight: 0, position: 'relative' }}>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>
              {SIDEBAR_ITEMS.find(i => i.key === tab)?.icon} {SIDEBAR_ITEMS.find(i => i.key === tab)?.label}
            </div>

        {/* Статистика */}
        {tab === 'stats' && stats && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              ['👥 Всего юзеров', stats.total_users],
              ['⭐ Premium', stats.premium_users],
              ['🆓 Free', stats.free_users],
              ['📝 Слов всего', stats.total_words],
              ['🟢 Активных сегодня', stats.today_active_users],
              ['✅ Ответов сегодня', stats.today_answered_cards],
            ].map(([label, value]) => (
              <div key={String(label)} style={{
                background: 'var(--theme-bg)', borderRadius: 12, padding: '14px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 }}>
                  {String(label)}
                </div>
                <div style={{ fontSize: 24, fontWeight: 700 }}>{String(value)}</div>
              </div>
            ))}
          </div>
        )}

        {/* Пользователи */}
        {tab === 'users' && (
          <div>
            {users.map(u => (
              <div key={u.id} style={{
                background: 'var(--theme-bg)', borderRadius: 10, padding: '12px 14px',
                marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {u.first_name} {u.last_name ?? ''} {u.is_admin ? '🛡️' : ''}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)' }}>
                    @{u.username ?? 'без ника'} · {u.word_count} слов
                  </div>
                </div>
                <div style={{
                  fontSize: 12, padding: '3px 8px',
                  background: u.subscription?.plan === 'premium' ? '#FFD70022' : 'var(--theme-bg-elevated)',
                  color: u.subscription?.plan === 'premium' ? '#FFD700' : 'var(--theme-text-secondary)',
                  borderRadius: 6, fontWeight: 600,
                }}>
                  {u.subscription?.plan ?? 'free'}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Feature flags */}
        {tab === 'flags' && (
          <div>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr auto auto',
              gap: '8px 12px', alignItems: 'center',
              background: 'var(--theme-bg)', borderRadius: 10,
              padding: '10px 12px', marginBottom: 8,
              fontSize: 11, fontWeight: 700, color: 'var(--theme-text-secondary)',
            }}>
              <span>Флаг</span><span>Free</span><span>Premium</span>
            </div>
            {flags.map(flag => (
              <div key={flag.key} style={{
                display: 'grid', gridTemplateColumns: '1fr auto auto',
                gap: '8px 12px', alignItems: 'center',
                background: 'var(--theme-bg)', borderRadius: 10,
                padding: '10px 12px', marginBottom: 6,
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{flag.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--theme-text-secondary)' }}>{flag.key}</div>
                </div>
                <Toggle
                  checked={flag.free_access}
                  onChange={() => toggleFlag(flag.key, 'free_access', flag.free_access)}
                />
                <Toggle
                  checked={flag.premium_access}
                  onChange={() => toggleFlag(flag.key, 'premium_access', flag.premium_access)}
                />
              </div>
            ))}
          </div>
        )}

        {/* Промокоды */}
        {tab === 'promos' && (
          <div>
            {/* Форма создания */}
            <div style={{ background: 'var(--theme-bg)', borderRadius: 12, padding: '14px', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>Новый промокод</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input
                  value={newPromo.code}
                  onChange={e => setNewPromo(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  placeholder="КОД"
                  style={inputStyle}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="number"
                    value={newPromo.duration_days}
                    onChange={e => setNewPromo(p => ({ ...p, duration_days: Number(e.target.value) }))}
                    placeholder="Дней"
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <input
                    type="number"
                    value={newPromo.max_uses}
                    onChange={e => setNewPromo(p => ({ ...p, max_uses: e.target.value }))}
                    placeholder="Макс. использований"
                    style={{ ...inputStyle, flex: 2 }}
                  />
                </div>
                <button onClick={createPromo} style={{
                  padding: '10px', background: 'var(--theme-primary)',
                  color: 'var(--theme-primary-text)', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600,
                }}>
                  Создать
                </button>
                {promoMessage && (
                  <div style={{ fontSize: 13, textAlign: 'center' }}>{promoMessage}</div>
                )}
              </div>
            </div>

            {/* Список промокодов */}
            {promos.map(promo => (
              <div key={promo.id} style={{
                background: 'var(--theme-bg)', borderRadius: 10,
                padding: '12px 14px', marginBottom: 8,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontWeight: 700, letterSpacing: 1 }}>{promo.code}</div>
                  <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)' }}>
                    {promo.duration_days} дней · {promo.used_count}/{promo.max_uses ?? '∞'} использований
                  </div>
                </div>
                <button onClick={() => togglePromo(promo.id)} style={{
                  padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: promo.is_active ? '#4caf5022' : '#f4433622',
                  color: promo.is_active ? '#4caf50' : '#f44336',
                  fontWeight: 600, fontSize: 12,
                }}>
                  {promo.is_active ? 'Активен' : 'Отключён'}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Словарь — редактор */}
        {tab === 'dictionary' && <DictionaryEditor />}
          </div>
        </div>
      </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', boxSizing: 'border-box',
  background: 'var(--theme-bg-elevated)', color: 'var(--theme-text)',
  border: '1px solid var(--theme-border)', borderRadius: 8, fontSize: 14,
};

function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <div onClick={onChange} style={{
      width: 40, height: 22, borderRadius: 11,
      background: checked ? 'var(--theme-primary)' : 'var(--theme-border)',
      position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
    }}>
      <div style={{
        position: 'absolute', top: 3, left: checked ? 20 : 3,
        width: 16, height: 16, borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s',
      }} />
    </div>
  );
}
